import { apiRequest, refreshSession } from "./apiClient";
import { BACKEND_API_BASE } from "../config/api";

/**
 * Construction (sketch) module API — backend: /construction, cookie-authenticated.
 * Sketches live in their own tables; nothing here touches the live layers until
 * an admin approves.
 */
export const listLayers = () => apiRequest("/construction/layers");

/** Server feature switches: { review_enabled } */
export const getConfig = () => apiRequest("/construction/config");

/** Paged: { items, page, page_size, total, pages } */
export const listSketches = ({
  status = null,
  page = 1,
  pageSize = 10,
  scope = "mine", // "mine" = own sketches; "all" = admin review queue (other users, no drafts)
} = {}) => {
  const params = new URLSearchParams({
    page: String(page),
    page_size: String(pageSize),
    scope,
  });
  if (status) params.set("status", status);
  return apiRequest(`/construction/sketches?${params}`);
};

/** Owner only, while the sketch is draft or rejected. */
export const deleteSketch = (id) =>
  apiRequest(`/construction/sketches/${id}`, { method: "DELETE" });

/** @param {{name:string, description?:string}} data */
export const createSketch = (data) =>
  apiRequest("/construction/sketches", {
    method: "POST",
    body: JSON.stringify(data),
  });

/** -> { sketch, features: GeoJSON FeatureCollection } */
export const getSketch = (id) => apiRequest(`/construction/sketches/${id}`);

/** Replaces all features of the sketch. @param {{layer_key,geometry,properties}[]} features */
export const saveSketchFeatures = (id, features) =>
  apiRequest(`/construction/sketches/${id}/features`, {
    method: "PUT",
    body: JSON.stringify({ features }),
  });

export const submitSketch = (id) =>
  apiRequest(`/construction/sketches/${id}/submit`, { method: "POST" });

export const approveSketch = (id, remarks = null) =>
  apiRequest(`/construction/sketches/${id}/approve`, {
    method: "POST",
    body: JSON.stringify({ remarks }),
  });

export const rejectSketch = (id, remarks) =>
  apiRequest(`/construction/sketches/${id}/reject`, {
    method: "POST",
    body: JSON.stringify({ remarks }),
  });

/** Download the saved sketch as GeoJSON (file name comes from the server). */
export async function exportSketch(id) {
  const url = `${BACKEND_API_BASE}/construction/sketches/${id}/export`;
  let response = await fetch(url, { credentials: "include" });
  if (response.status === 401) {
    await refreshSession();
    response = await fetch(url, { credentials: "include" });
  }
  if (!response.ok) {
    let message = "Export failed";
    try {
      const data = await response.json();
      if (typeof data.detail === "string") message = data.detail;
    } catch {
      /* keep default message */
    }
    throw new Error(message);
  }
  const match = /filename="([^"]+)"/.exec(
    response.headers.get("content-disposition") || "",
  );
  const filename = match ? match[1] : `sketch_${id}.geojson`;
  const blobUrl = URL.createObjectURL(await response.blob());
  const a = document.createElement("a");
  a.href = blobUrl;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(blobUrl);
  return filename;
}
